// "Confirm your details": the popup after a time is picked. The fields of
// the old /book-a-demo form, plus guests, job title and the tools BossDrop
// connects to today. The backend (server/booking/bookDemo.js) checks the time
// again before it books, and validates every field the same way.
import React, { useEffect, useRef, useState } from "react";
import slackLogo from "../../images/slack.svg";
import teamsLogo from "../../images/microsoftteams.svg";
import gmailLogo from "../../images/gmail.svg";
import outlookLogo from "../../images/outlook.svg";
import googleCalendarLogo from "../../images/google-calendar.svg";
import asanaLogo from "../../images/asana.svg";
import jiraLogo from "../../images/jira.svg";
import linearLogo from "../../images/linear.svg";
import clickupLogo from "../../images/clickup-mark.svg";
import notionLogo from "../../images/notion-mark.svg";
import mondayLogo from "../../images/monday-mark.svg";
import plannerLogo from "../../images/microsoft-planner.svg";
import githubLogo from "../../images/github.svg";
import hubspotLogo from "../../images/hubspot-72.webp";
import pipedriveLogo from "../../images/pipedrive.svg";
import { createBooking, HOST_EMAIL } from "./bookingApi";
import { currentRef, withRef } from "../referral";
import ToolsSelect from "../ToolsSelect";
import { dayKey, formatDay, formatTime, zoneLongName } from "./zones";
import { trapTab } from "./a11y";
import { ClockIcon, CloseIcon, GlobeIcon, PlusIcon, VideoIcon } from "./icons";

export const TEAM_SIZES = ["1–25", "26–100", "101–500", "500+"];
export const MAX_GUESTS = 2;

// The keys the backend accepts (BOOKING_TOOLS in server/booking/bookDemo.js).
export const TOOLS = [
  { key: "slack", label: "Slack", logo: slackLogo },
  { key: "teams", label: "Microsoft Teams", logo: teamsLogo },
  { key: "gmail", label: "Gmail", logo: gmailLogo },
  { key: "outlook", label: "Outlook", logo: outlookLogo },
  { key: "google_calendar", label: "Google Calendar", logo: googleCalendarLogo },
  { key: "outlook_calendar", label: "Outlook Calendar", logo: outlookLogo },
  { key: "asana", label: "Asana", logo: asanaLogo },
  { key: "jira", label: "Jira", logo: jiraLogo },
  { key: "linear", label: "Linear", logo: linearLogo },
  { key: "clickup", label: "ClickUp", logo: clickupLogo },
  { key: "notion", label: "Notion", logo: notionLogo },
  { key: "monday", label: "monday.com", logo: mondayLogo },
  { key: "planner", label: "Microsoft Planner", logo: plannerLogo },
  { key: "github", label: "GitHub", logo: githubLogo },
  { key: "hubspot", label: "HubSpot", logo: hubspotLogo },
  { key: "pipedrive", label: "Pipedrive", logo: pipedriveLogo },
];

const FALLBACK = `Booking didn't go through. Try again, or email ${HOST_EMAIL}.`;
const ERRORS = {
  name_required: "Add your name.",
  email_invalid: "Use a valid work email.",
  company_required: "Add your company.",
  team_size_required: "Pick your team size.",
  too_many_guests: `Add at most ${MAX_GUESTS} guests.`,
  guest_email_invalid: "Check the guest email addresses.",
  booking_daily_cap: `Booking is very busy right now. Email ${HOST_EMAIL} to find a time.`,
  rate_limited: `Booking is very busy right now. Email ${HOST_EMAIL} to find a time.`,
  booking_unavailable: FALLBACK,
  network: `Couldn't reach BossDrop. Check your connection and try again, or email ${HOST_EMAIL}.`,
};

export default function BookingDetails({ slot, tz, hour12, onBack, onBooked, onTaken }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", teamSize: "", goals: "", heard: "", website: "" });
  const [tools, setTools] = useState([]);
  const [guests, setGuests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const startMs = Date.parse(slot.start);
  const endMs = Date.parse(slot.end);
  const day = formatDay(dayKey(startMs, tz), { weekday: "long", month: "long", day: "numeric" });
  const when = `${day}, ${formatTime(startMs, tz, hour12)} to ${formatTime(endMs, tz, hour12)}`;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.teamSize) {
      setError(ERRORS.team_size_required);
      return;
    }
    setBusy(true);
    setError("");
    const out = await createBooking({
      ...form,
      heard: withRef(form.heard),
      // The backend registers the company on that specialist's dashboard.
      ref: currentRef() || undefined,
      tools,
      guests: guests.map((g) => g.trim()).filter(Boolean),
      timeZone: tz,
      start: slot.start,
    });
    setBusy(false);
    if (out.ok) {
      try {
        window.gtag?.("event", "generate_lead", { method: "demo_booking" });
        window.fbq?.("track", "Lead");
      } catch {}
      onBooked({ ...out, email: form.email.trim().toLowerCase() });
      return;
    }
    if (out.error === "slot_taken" || out.error === "start_invalid") {
      onTaken();
      return;
    }
    setError(ERRORS[out.error] || (out.status === 429 ? ERRORS.rate_limited : FALLBACK));
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (!busy) onBack();
      return;
    }
    trapTab(e, dialogRef.current);
  };

  return (
    <div className="bk-dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onBack()}>
      <div
        className="bk-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bk-details-title"
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={onKeyDown}
      >
        <button type="button" className="bk-icon-btn bk-dialog-close" aria-label="Back to times" onClick={onBack} disabled={busy}>
          <CloseIcon />
        </button>
        <h2 className="bk-dialog-title" id="bk-details-title">
          Confirm your details
        </h2>
        <ul className="bk-dialog-when">
          <li>
            <ClockIcon size={16} /> {when}
          </li>
          <li>
            <GlobeIcon size={16} /> {zoneLongName(tz, startMs)}
          </li>
          <li>
            <VideoIcon size={16} /> Google Meet
          </li>
        </ul>

        <form className="bk-form" onSubmit={submit} noValidate={false}>
          <div className="bk-field">
            <label htmlFor="bk-name">Your name *</label>
            <input id="bk-name" name="name" required autoComplete="name" maxLength={120} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
          </div>
          <div className="bk-field">
            <label htmlFor="bk-email">Work email *</label>
            <input
              id="bk-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              maxLength={200}
              value={form.email}
              onChange={set("email")}
              placeholder="jane@company.com"
            />
          </div>

          <div className="bk-field bk-wide">
            {guests.map((g, i) => (
              <div className="bk-guest" key={i}>
                <label className="sr-only" htmlFor={`bk-guest-${i}`}>
                  Guest {i + 1} email
                </label>
                <input
                  id={`bk-guest-${i}`}
                  // A guest field gets focus when it is added.
                  autoFocus
                  type="email"
                  autoComplete="off"
                  maxLength={200}
                  value={g}
                  onChange={(e) => setGuests((list) => list.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder="colleague@company.com"
                />
                <button
                  type="button"
                  className="bk-icon-btn"
                  aria-label={`Remove guest ${i + 1}`}
                  onClick={() => setGuests((list) => list.filter((_, j) => j !== i))}
                >
                  <CloseIcon size={16} />
                </button>
              </div>
            ))}
            {guests.length < MAX_GUESTS ? (
              <button type="button" className="bk-add-guest" onClick={() => setGuests((list) => [...list, ""])}>
                <PlusIcon size={16} /> Add a guest
              </button>
            ) : null}
          </div>

          <div className="bk-field">
            <label htmlFor="bk-company">Company *</label>
            <input id="bk-company" name="company" required autoComplete="organization" maxLength={160} value={form.company} onChange={set("company")} placeholder="Acme Inc." />
          </div>
          <div className="bk-field">
            <label htmlFor="bk-role">Job title</label>
            <input id="bk-role" name="role" autoComplete="organization-title" maxLength={120} value={form.role} onChange={set("role")} placeholder="VP Operations" />
          </div>

          <div className="bk-field bk-wide">
            <span className="bk-label" id="bk-size-label">
              Team size *
            </span>
            <div className="bk-chips" role="radiogroup" aria-labelledby="bk-size-label">
              {TEAM_SIZES.map((size) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.teamSize === size}
                  className="bk-chip"
                  key={size}
                  onClick={() => setForm((f) => ({ ...f, teamSize: size }))}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div className="bk-field bk-wide">
            <label htmlFor="bk-tools">Tools your team uses</label>
            <ToolsSelect id="bk-tools" tools={TOOLS} value={tools} onChange={setTools} />
          </div>

          <div className="bk-field bk-wide">
            <label htmlFor="bk-goals">What would you like to see?</label>
            <textarea
              id="bk-goals"
              name="goals"
              rows={3}
              maxLength={1500}
              value={form.goals}
              onChange={set("goals")}
              placeholder="Workflows, tools or outcomes you want covered"
            />
          </div>
          <div className="bk-field bk-wide">
            <label htmlFor="bk-heard">How did you hear about BossDrop?</label>
            <input id="bk-heard" name="heard" maxLength={200} value={form.heard} onChange={set("heard")} placeholder="LinkedIn, a friend, a search..." />
          </div>

          {/* Left empty by people; bots fill it, and the backend drops those bookings. */}
          <div className="bk-hp" aria-hidden="true">
            <label htmlFor="bk-website">Website</label>
            <input id="bk-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
          </div>

          <p className="bk-consent bk-wide">
            By entering your email, you agree to receive product updates and marketing emails from BossDrop. You can
            unsubscribe at any time. We respect your privacy.
          </p>

          {error ? (
            <p className="bk-error bk-wide" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bk-actions bk-wide">
            <button type="button" className="bk-ghost" onClick={onBack} disabled={busy}>
              Back
            </button>
            <button type="submit" className="bd-button bk-confirm" disabled={busy}>
              {busy ? "Booking..." : "Confirm"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
